import { FeePaymentType, PaymentMethodType, PercentDto, Pricing, PricingRange } from '@nemuru-eng/js-api-client';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { GetPricingsService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsService';
import { Filter, FilterType } from '@/components/modules/Application/components/PaymentMethod/_shared/types/Filter';
import { LoanConditions } from '@/components/modules/Application/components/PaymentMethod/_shared/types/LoanConditions';
import { PaymentMethodRequestType } from '@/components/modules/Application/components/PaymentMethod/_shared/types/PaymentMethodRequestType';
import { ConsumerLoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/ConsumerLoan/entities/ConditionsGroup/ConsumerLoanConditionsGroup';
import { isArrayContinuous } from '@/utils/isArrayContinuous';

const pricingsService = GetPricingsService.getInstance();

type ConsumerLoanPaymentMethodRequest = PaymentMethodRequestType<
  PaymentMethodType.CONSUMER_LOAN_INSTALMENTS | PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY
>;

export class ConsumerLoanPaymentMethod extends AbstractLoanPaymentMethod {
  protected ALLOWED_PAYMENT_METHOD_TYPES = [
    PaymentMethodType.CONSUMER_LOAN_INSTALMENTS,
    PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY,
  ];

  public areOptionsConfigurable(): boolean {
    return true;
  }

  protected setAvailableFilters(allConditions: LoanConditions[]): void {
    const filters: Filter[] = [];
    if (this.hasDifferentSetupFees(allConditions)) {
      filters.push({ type: FilterType.WITH_SETUP_FEE, isActive: false });
    }
    if (this.hasDifferentGracePeriods(allConditions)) {
      filters.push({ type: FilterType.WITH_GRACE_PERIOD, isActive: false });
    }
    if (this.hasDifferentMerchantDiscountPercentage(allConditions)) {
      filters.push({ type: FilterType.WITH_MERCHANT_DISCOUNT_PERCENTAGE, isActive: false });
    }
    this.filters = filters;
  }

  protected getAvailableConditions(allConditions: LoanConditions[]): LoanConditions[] {
    const filters = this.filters;

    const filteredConditions = allConditions;

    if (!filters || filters.length === 0) {
      return filteredConditions;
    }

    const availableConditions: LoanConditions[] = [];

    const hasSetupFee = filters.some((filter) => filter.type === FilterType.WITH_SETUP_FEE && filter.isActive);
    const hasGracePeriod = filters.some((filter) => filter.type === FilterType.WITH_GRACE_PERIOD && filter.isActive);
    const hasMerchantDiscountPercentage = filters.some(
      (filter) => filter.type === FilterType.WITH_MERCHANT_DISCOUNT_PERCENTAGE && filter.isActive,
    );

    filteredConditions.forEach((condition) => {
      if (hasSetupFee && ConsumerLoanConditionsGroup.getHasSetupFee([condition]) !== hasSetupFee) return;
      if (hasGracePeriod && ConsumerLoanConditionsGroup.getHasGracePeriod([condition]) !== hasGracePeriod) return;
      if (
        hasMerchantDiscountPercentage &&
        ConsumerLoanConditionsGroup.getHasMerchantDiscountPercentage([condition]) !== hasMerchantDiscountPercentage
      )
        return;
      availableConditions.push(condition);
    });

    return availableConditions;
  }

  private hasDifferentSetupFees(allConditions: LoanConditions[]): boolean {
    const setupFees = [...new Set(allConditions.map((condition) => condition.setupFee?.value))];
    return this.hasDifferentValues(setupFees);
  }

  private hasDifferentGracePeriods(allConditions: LoanConditions[]): boolean {
    const gracePeriods = [...new Set(allConditions.map((condition) => condition.gracePeriod))];
    return this.hasDifferentValues(gracePeriods);
  }

  private hasDifferentMerchantDiscountPercentage(allConditions: LoanConditions[]): boolean {
    const merchantDiscountPercentages = [
      ...new Set(allConditions.map((condition) => condition.merchantDiscountPercentage?.value)),
    ];
    return this.hasDifferentValues(merchantDiscountPercentages);
  }

  private hasDifferentValues(values: (number | undefined | null)[]): boolean {
    if (values.length <= 1) return false;
    return values.some((v) => v === undefined || v === null || v === 0) && values.some((v) => v && v > 0);
  }

  protected getSelectedTermCandidate(availableTerms: number[]): number {
    // FIXME: refactor
    return availableTerms[availableTerms.length - 1];
  }

  protected getConditionsGroups(availableConditions: LoanConditions[], term: number): ConsumerLoanConditionsGroup[] {
    const pricings = pricingsService.pricings;

    if (!pricings) {
      throw new Error('Pricings are not available');
    }

    const filteredConditions = availableConditions
      .filter((condition) => condition.term === term)
      .map((condition) => this.addMerchantDiscountPercentage(condition, pricings));

    const conditionsGroups = filteredConditions.reduce((cum, condition) => {
      const candidateGroup = cum.find((g) => {
        return (
          g.hasSetupFee === ConsumerLoanConditionsGroup.getHasSetupFee([condition]) &&
          g.hasGracePeriod === ConsumerLoanConditionsGroup.getHasGracePeriod([condition]) &&
          g.hasMerchantDiscountPercentage === ConsumerLoanConditionsGroup.getHasMerchantDiscountPercentage([condition])
        );
      });

      if (candidateGroup) {
        candidateGroup.addCondition(condition);
      } else {
        cum.push(new ConsumerLoanConditionsGroup([condition]));
      }

      return cum;
    }, [] as ConsumerLoanConditionsGroup[]);

    return conditionsGroups;
  }

  protected getSelectedConditionsGroupCandidate(
    conditionsGroups: ConsumerLoanConditionsGroup[],
  ): ConsumerLoanConditionsGroup {
    const selectedConditionsGroupCandidate = conditionsGroups.reduce((cum, group) => {
      if (group.getCandidateConditions().apr.value < cum.getCandidateConditions().apr.value) return group;
      return cum;
    }, conditionsGroups[0]);

    return selectedConditionsGroupCandidate;
  }

  private addMerchantDiscountPercentage(condition: LoanConditions, pricings: Pricing[]): LoanConditions {
    const range = this.getRange(condition, pricings);
    return {
      ...condition,
      ...(range.merchantDiscountPercentage && {
        merchantDiscountPercentage: new PercentDto(range.merchantDiscountPercentage),
      }),
    };
  }

  private getRange(condition: LoanConditions, pricings: Pricing[]): PricingRange {
    const pricing = pricings.find((p) => p.id === condition.pricingId);
    if (!pricing) {
      throw new Error(`Pricing not found with id: ${condition.pricingId}`);
    }
    const range = pricing.ranges.find(
      (r) =>
        this.sameAnnualInterest(r.annualInterest, condition.annualInterest?.value) &&
        this.sameSetupFee(r.setupFee.paymentType, condition.setupFeeType) &&
        this.samePeriodMin(r.rangePeriodMin, condition.term) &&
        this.samePeriodMax(r.rangePeriodMax, condition.term) &&
        r.rangePrincipalMin < condition.principal.value &&
        r.rangePrincipalMax >= condition.principal.value,
    );
    if (!range) {
      throw new Error('Range not found');
    }
    return range;
  }

  private sameAnnualInterest(pricingAnnualInterest: number | null, annualInterest: number | undefined): boolean {
    if (!pricingAnnualInterest && !annualInterest) return true;
    if (pricingAnnualInterest && annualInterest && pricingAnnualInterest === annualInterest) return true;
    return false;
  }

  private sameSetupFee(pricingSetupFee: FeePaymentType | null, setupFee: FeePaymentType | undefined): boolean {
    if (!pricingSetupFee && !setupFee) return true;
    if (pricingSetupFee && setupFee && pricingSetupFee === setupFee) return true;
    return false;
  }

  private samePeriodMin(pricingPeriodMin: number | null, period: number | undefined): boolean {
    if (!pricingPeriodMin && !period) return true;
    if (pricingPeriodMin && period && pricingPeriodMin <= period) return true;
    return false;
  }

  private samePeriodMax(pricingPeriodMax: number | null, period: number | undefined): boolean {
    if (!pricingPeriodMax && !period) return true;
    if (pricingPeriodMax && period && pricingPeriodMax >= period) return true;
    return false;
  }

  public get proposedTerms(): number[] {
    if (!isArrayContinuous(this.availableTerms)) return this.availableTerms;

    const availableTerms = this.availableTerms;

    if (availableTerms.length <= 5) {
      return availableTerms;
    }

    const proposedTerms: number[] = [];

    if (availableTerms.includes(3)) proposedTerms.push(3);
    if (availableTerms.includes(6)) proposedTerms.push(6);

    let proposedTermsByIncrement = availableTerms.filter((term) => term >= 12).filter((term) => term % 12 === 0);
    if (proposedTermsByIncrement.length >= 4) {
      return [...proposedTerms, ...proposedTermsByIncrement];
    }

    proposedTermsByIncrement = availableTerms.filter((term) => term >= 12).filter((term) => term % 6 === 0);
    if (proposedTermsByIncrement.length >= 4) {
      return [...proposedTerms, ...proposedTermsByIncrement];
    }

    proposedTermsByIncrement = availableTerms.filter((term) => term >= 3).filter((term) => term % 3 === 0);

    if (proposedTermsByIncrement.length >= 4) {
      return [...proposedTermsByIncrement];
    }

    return availableTerms;
  }

  public toDto(): ConsumerLoanPaymentMethodRequest {
    if (!this.selectedTerm) {
      throw new Error('Term is not selected');
    }

    return {
      type: this.paymentMethodType as ConsumerLoanPaymentMethodRequest['type'],
      options: {
        period: this.selectedTerm,
        hasSetupFee: (this.selectedConditionsGroup as ConsumerLoanConditionsGroup).hasSetupFee,
        hasMerchantDiscountPercentage: (this.selectedConditionsGroup as ConsumerLoanConditionsGroup)
          .hasMerchantDiscountPercentage,
        hasGracePeriod: (this.selectedConditionsGroup as ConsumerLoanConditionsGroup).hasGracePeriod,
      },
    };
  }
}
